import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import {
    AttachUsersToUserGroupDocument,
    type AttachUsersToUserGroupMutation,
    type AttachUsersToUserGroupMutationVariables,
} from '../../../api/mutations/attachUsersToUserGroup';
import useHandleError from '../../../utilities/useHandleError';
import SearchUserWithTableFieldByUserGroup from './SearchUserWithTableFieldByUserGroup';

type FormValues = { users: string[] };

const initialValues: FormValues = { users: [] };

export type UserGroupAddUserFormProps = {
    userGroup: UserGroupSpecsFragment;
    onComplete: () => void;
};

const UserGroupAddUserForm = ({ userGroup, onComplete }: UserGroupAddUserFormProps) => {
    const { t } = useTranslation(['userGroupDetails']);
    const apolloClient = useApolloClient();
    const userGroupId = userGroup.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userGroupDetails:userDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<AttachUsersToUserGroupMutation, AttachUsersToUserGroupMutationVariables>({
                    mutation: AttachUsersToUserGroupDocument,
                    variables: { userGroupId, userIds: values.users },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userGroupDetails:userDrawer.messages.success'),
                key: 'primary',
            });

            // then close the drawer
            onComplete();
        },
        [apolloClient, t, onComplete, userGroupId]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="addUsers" layout="vertical" name="addUsers" onSubmitCapture={handleSubmit}>
                    <SearchUserWithTableFieldByUserGroup debounceTimeout={800} name="users" userGroup={userGroup} />
                </Form>
            )}
        </Formik>
    );
};

export default UserGroupAddUserForm;
